"use client";

import {
  useEffect,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { cn } from "../cn";

export interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (otp: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
}

// 6-digit segmented OTP input. Auto-advances, supports paste, keyboard arrows, backspace.
export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  autoFocus = true,
  disabled,
  invalid,
  ariaLabel = "One-time password",
}: OTPInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  // Identity-stable callback + fire-once-per-value: consumers pass inline
  // handlers whose identity changes every render — depending on it caused an
  // infinite update loop (onComplete → setState → render → effect → …).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const lastCompleted = useRef<string | null>(null);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (value.length === length && lastCompleted.current !== value) {
      lastCompleted.current = value;
      onCompleteRef.current?.(value);
    }
    if (value.length < length) lastCompleted.current = null;
  }, [value, length]);

  const setChar = (idx: number, char: string) => {
    const padded = value.padEnd(length, " ").split("");
    padded[idx] = char;
    const next = padded.join("").replace(/\s+$/, "");
    onChange(next);
  };

  const handleChange = (idx: number, e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digit = raw.replace(/\D/g, "").slice(-1);
    if (!digit) return;
    setChar(idx, digit);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKey = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[idx]) setChar(idx, "");
      else if (idx > 0) {
        refs.current[idx - 1]?.focus();
        setChar(idx - 1, "");
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const nextIdx = Math.min(text.length, length - 1);
    refs.current[nextIdx]?.focus();
  };

  return (
    <div role="group" aria-label={ariaLabel} className="flex items-center gap-2">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          inputMode="numeric"
          autoComplete={idx === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={value[idx] ?? ""}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKey(idx, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-invalid={invalid ? "true" : undefined}
          aria-label={`Digit ${idx + 1}`}
          className={cn(
            "border-charcoal/20 bg-cream text-body-lg text-charcoal h-12 w-10 rounded-md border text-center font-mono",
            "focus-visible:ring-saffron focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            invalid && "border-terracotta focus-visible:ring-terracotta",
          )}
        />
      ))}
    </div>
  );
}
